import {
    MindyViewContext,
    MINDY_CONTEXT_CHANNEL,
    MINDY_CONTEXT_STORAGE_KEY,
    MINDY_CONTEXT_VERSION,
} from '@quadminds/shared/mindy-context';
import { useEffect, useRef } from 'react';

/**
 * Suscribe al contexto de pantalla que publica el SaaS (route-tracking) y lo
 * expone como ref (QS-19310).
 *
 * Dos canales, no uno: `BroadcastChannel` no retiene mensajes, así que si el
 * iframe de Mindy monta después del último broadcast no recibiría nada.
 * `localStorage` cubre ese caso leyendo el último estado conocido al montar.
 * Ambos funcionan porque los dos iframes son same-origin (platform.quadminds.com).
 *
 * Devuelve un ref, no un useState: el dashboard hace polling, el contexto se
 * actualiza seguido y no tiene que disparar un re-render del chat.
 */
export function useMindyViewContext() {
    const contextRef = useRef<MindyViewContext | null>(null);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(MINDY_CONTEXT_STORAGE_KEY);
            const parsed = stored ? (JSON.parse(stored) as MindyViewContext) : null;
            if (parsed?.context_version === MINDY_CONTEXT_VERSION) {
                contextRef.current = parsed;
            }
        } catch {
            /* la lectura del último estado conocido nunca puede romper el montaje */
        }

        let bus: BroadcastChannel | undefined;
        try {
            bus = new BroadcastChannel(MINDY_CONTEXT_CHANNEL);
            bus.onmessage = (event: MessageEvent<MindyViewContext>) => {
                if (event.data?.context_version === MINDY_CONTEXT_VERSION) {
                    contextRef.current = event.data;
                }
            };
        } catch {
            /* noop */
        }

        return () => bus?.close();
    }, []);

    return contextRef;
}
